"use client";

import { Box } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Modal, ModalBody, ModalFooter } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { Button } from "../../ui/Button";
import { Text } from "../../ui/Text";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  confirmText: string;
  cancelText?: string;
  confirmColorScheme?: string;
  isLoading?: boolean;
  loadingTitle?: string;
  loadingMessage?: string | ReactNode;
  loadingText?: string;
  /** Replaces the confirm actions with the loading message only. */
  hideActionsWhileLoading?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  "data-testid"?: string;
}

export const LOGOUT_CONFIRMATION_LOADING = {
  loadingTitle: "Signing you out...",
  loadingMessage: "Securing your session...",
  loadingText: "Signing out...",
} as const;

export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = "Cancel",
  confirmColorScheme = "brand",
  isLoading = false,
  loadingTitle,
  loadingMessage,
  loadingText = "Processing...",
  hideActionsWhileLoading = false,
  size = "md",
  "data-testid": dataTestId,
}: ConfirmationModalProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const displayTitle = isLoading && loadingTitle != null ? loadingTitle : title;
  const displayMessage =
    isLoading && loadingMessage != null ? loadingMessage : message;
  const showLoadingOnly = isLoading && hideActionsWhileLoading;

  const getConfirmButtonStyles = () => {
    // Use theme accent colors instead of hardcoded red/green
    if (confirmColorScheme === "red" || confirmColorScheme === "green") {
      return {
        bg: theme.accent.primary,
        color: "white",
        sx: {
          "@media (hover: hover)": {
            "&:hover": {
              bg: interactive.button.primary.hover,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active": {
              bg: interactive.button.primary.active,
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      };
    }
    return {};
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={displayTitle}
      size={{ base: size, md: size }}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
      showCloseButton={!isLoading}
      contentProps={
        dataTestId ? ({ "data-testid": dataTestId } as any) : undefined
      }
    >
      <ModalBody py={6}>
        {showLoadingOnly ? (
          displayMessage
        ) : (
          <Box
            key={isLoading ? "loading" : "confirm"}
            sx={{
              animation: "logoutModalMorph 0.32s ease-out",
              "@keyframes logoutModalMorph": {
                from: {
                  opacity: 0,
                  transform: "translateY(6px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            }}
          >
            <Text
              as="div"
              fontSize="sm"
              color={theme.text.secondary}
              lineHeight="1.6"
            >
              {displayMessage}
            </Text>
          </Box>
        )}
      </ModalBody>

      {showLoadingOnly ? null : (
      <ModalFooter
        px={{ base: 4, md: 6 }}
        py={4}
        borderTopWidth="1px"
        borderTopColor={theme.borderGlass.default}
        gap={3}
        justifyContent={isLoading ? "flex-end" : undefined}
      >
        {!isLoading ? (
          <Button
            variant="outline"
            onClick={onClose}
            flex={{ base: "1", sm: "none" }}
            {...(dataTestId
              ? { "data-testid": `${dataTestId}-cancel-button` }
              : {})}
          >
            {cancelText}
          </Button>
        ) : null}

        <Button
          variant="primary"
          onClick={onConfirm}
          isLoading={isLoading}
          loadingText={loadingText}
          flex={isLoading ? "none" : { base: "1", sm: "none" }}
          {...(dataTestId
            ? { "data-testid": `${dataTestId}-confirm-button` }
            : {})}
          {...getConfirmButtonStyles()}
        >
          {confirmText}
        </Button>
      </ModalFooter>
      )}
    </Modal>
  );
};
